import dateFormat from "dateformat";
import {
  formatDistanceToNow,
  format,
  isValid,
  differenceInDays,
  parseISO,
} from "date-fns";

export const getParsedDate = (date, options = {}) => {
  try {
    // Parse the ISO string to a Date object
    const parsedDate = parseISO(date);

    // Validate the parsed date
    if (!isValid(parsedDate)) {
      throw new Error("Invalid date");
    }

    const currentDate = new Date();

    // Calculate difference in days
    const daysDiff = differenceInDays(currentDate, parsedDate);

    // Check if date is more than 7 days ago
    if (daysDiff > 7) {
      return format(parsedDate, options.dateFormat || "MMM dd, yyyy");
    }

    // Calculate distance to now
    const timeDiff = formatDistanceToNow(parsedDate, {
      addSuffix: true,
      includeSeconds: options.includeSeconds || false,
    });

    return timeDiff;
  } catch (error) {
    // Handle errors gracefully
    console.error("Error in getParsedDate:", error.message);
    return "Invalid date";
  }
};

// ! Note:- we are keeping getParsedDate1 function for backup
// return the time differance.
export const getParsedDate1 = (date) => {
  const currentDate = new Date();
  const targetDate = new Date(date);

  const utc1 = Date.UTC(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate()
  );
  const utc2 = Date.UTC(
    targetDate.getFullYear(),
    targetDate.getMonth(),
    targetDate.getDate()
  );
  const daysDiff = Math.abs(Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24)));

  if (daysDiff > 7) {
    return dateFormat(date, "mmm dd, yyyy");
  }

  const oldDate = String(date).split("T");
  const oldDay = String(oldDate[0]).split("-");
  const oldTime = String(oldDate[1]).split(":");
  const LastDate = [
    parseInt(oldDay[0]),
    parseInt(oldDay[1]),
    parseInt(oldDay[2]),
    parseInt(oldTime[0]),
    parseInt(oldTime[1]),
    parseInt(oldTime[2]),
  ];

  const day = currentDate.getUTCDate() - LastDate[2];
  const hours = currentDate.getUTCHours() - LastDate[3];
  const minutes = currentDate.getUTCMinutes() - LastDate[4];

  if (day < 0) {
    return dateFormat(date, "mmm dd, yyyy");
  }

  const unit = [];
  day === 0
    ? hours === 0
      ? unit.push(
          minutes > 1 ? minutes : "< 1 ",
          minutes > 1 ? "minutes" : "minute"
        )
      : unit.push(hours, hours > 1 ? "hours" : "hour")
    : unit.push(day, day > 1 ? "days" : "day");

  return unit.join(" ") + " ago";
};

// returns the Name intial

export const getIntials = (user) => {
  if (
    user &&
    user.firstName &&
    user.lastName &&
    user.firstName.length > 0 &&
    user.lastName.length > 0
  ) {
    return "".concat(
      user.firstName[0].toUpperCase(),
      user.lastName[0].toUpperCase()
    );
  }
  return "AA";
};

export const getName = (user) => {
  const firstName =
    user.firstName.slice(0, 1).toUpperCase() +
    user.firstName.slice(1, user.firstName.length);
  const lastName =
    user.lastName.slice(0, 1).toUpperCase() +
    user.lastName.slice(1, user.lastName.length);
  return "".concat(firstName, " ", lastName);
};

export const getCapitals = (str) => {
  const words = str.split(" ");
  return words
    .map((word) => {
      return word[0].toUpperCase() + word.substring(1);
    })
    .join(" ");
};

// return the time differance.
export const getParsedTime = (date) => {
  const currentDate = new Date();
  const targetDate = new Date(date);

  const hours = Math.round(
    Math.abs(currentDate - targetDate) / (1000 * 60 * 60)
  );
  const minutes = Math.round(Math.abs(currentDate - targetDate) / (1000 * 60));

  if (hours >= 1) {
    return dateFormat(date, "h.MM TT");
  }

  const unit = [];
  if (hours === 0 && minutes === 0) {
    return "Just Now";
  }
  hours === 0 ? unit.push(minutes, "m") : unit.push(hours, "h");
  return unit.join("") + " ago";
};

/**
 * Performs optional chaining on an object based on a provided path.
 *
 * @param {Object} obj - The input object on which to perform optional chaining.
 * @param {Array} path - An array representing the path to navigate through the object.
 * @param {*} defaultValue - The default value to return if the path is not fully traversable.
 * @returns {*} The value at the specified path, or the default value if any intermediate property is undefined.
 *
 * @example
 * const data = {
 *   user: {
 *     name: 'John',
 *     address: {
 *       city: 'Example City',
 *     },
 *   },
 * };
 *
 * const result1 = optionalChaining(data, ['user', 'address', 'city'], 'Unknown');
 * // Output: Example City
 *
 * const result2 = optionalChaining(data, ['user', 'address', 'country'], 'Unknown');
 * // Output: Unknown
 */
export function optionalChaining(obj, path, defaultValue) {
  return (
    path.reduce(
      (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
      obj
    ) || defaultValue
  );
}

/**
 * Converts a given value to a string based on certain conditions.
 *
 * @param {*} value - The value to be converted.
 * @returns {string} - The converted string value.
 */
export function convertToString(value) {
  /**
   * Converts a number to its string representation.
   *
   * @param {number} num - The number to be converted.
   * @returns {string} - The string representation of the number.
   */
  function convertNumberToString(num) {
    return String(num);
  }

  // Check the type of the value
  if (typeof value === "string") {
    // If the value is already a string, return it directly
    return value;
  } else if (typeof value === "number") {
    // If the value is a number, convert it to a string
    return convertNumberToString(value);
  } else {
    // For other types, return an empty string
    return "";
  }
}

/**
 * Creates a deep copy of an object, handling various data types and nested structures.
 *
 * @param {*} obj - The object to be deep copied.
 * @returns {*} - The deep copy of the input object.
 */
export function deepCopy(obj) {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (obj instanceof RegExp) {
    return new RegExp(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepCopy(item));
  }

  const newObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      newObj[key] = deepCopy(obj[key]);
    }
  }

  return newObj;
}
/*
 * Capitalizes the first letter of a string and converts the rest to lowercase.
 *
 * @param {string} inputString - The input string to be processed.
 * @returns {string} - The modified string with the first letter capitalized and the rest in lowercase, or an empty string if the input is not a string.
 *
 * @example
 * const originalString = "hELlo WoRLd";
 * const capitalizedString = capitalizeFirstLetter(originalString);
 * console.log(capitalizedString); // Output: "Hello world"
 */
export function capitalizeFirstLetter(inputString) {
  // Check if the input is a string
  if (typeof inputString !== "string") {
    return ""; // Return empty string for non-string input
  }

  // Check if the input string is empty
  if (inputString.length === 0) {
    return inputString; // Return input as is for an empty string
  }

  // Capitalize the first letter and convert the rest to lowercase
  return (
    inputString.charAt(0).toUpperCase() + inputString.slice(1).toLowerCase()
  );
}

/**
 * Validates if the provided email address has a valid format.
 *
 * @param {string} email - The email address to be validated.
 * @returns {boolean} - True if the email has a valid format, false otherwise.
 *
 * @example
 * const emailToCheck = "example@example.com";
 * const isValid = isEmailValid(emailToCheck);
 *
 * if (isValid) {
 *   console.log(`${emailToCheck} is a valid email.`);
 * } else {
 *   console.log(`${emailToCheck} is not a valid email.`);
 * }
 */
export function isEmailValid(email) {
  // Regular expression for a valid email address
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Test the email against the regex
  return emailRegex.test(email);
}

/**
 * Downloads a file from the given URL.
 * @param {string} url - The URL of the file to download.
 * @param {string} [filename='file.pdf'] - The name to save the file as.
 * @returns {void} - No return value.
 */
export const downloadFile = (url, filename = "file.pdf") => {
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  link.style.display = "none"; // Hide the link
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Format an ISO 8601 date string in the "Month Day, Year" format.
 * @param {string} isoDateString - The date string in ISO 8601 format (YYYY-MM-DD).
 * @returns {string} The formatted date string (e.g., "April 14, 2024").
 */
export function formatISODateToLongDate(isoDateString) {
  try {
    const date = parseISO(isoDateString);
    return format(date, "MMMM d, yyyy");
  } catch (error) {
    return "Invalid Date";
  }
}
